import { useEffect, useMemo, useRef, useState } from "react"
import { Box, Text } from "@chakra-ui/react"
import type { SkiFeatureDetail } from "../../../hooks/useResortFeatures"
import { useMapboxLoader } from "./useMapboxLoader"

type FeatureMapTabProps = {
  data: SkiFeatureDetail
}

export default function FeatureMapTab({ data }: FeatureMapTabProps) {
  const mapRef = useRef<HTMLDivElement | null>(null)
  const mapInstanceRef = useRef<any>(null)
  const [mapError, setMapError] = useState<string | null>(null)
  const { ready: mapboxReady, error: mapboxLoadError } = useMapboxLoader()

  const featureGeoJson = useMemo(() => {
    if (!data?.geometry) return null
    try {
      const parsed = JSON.parse(data.geometry)
      if (parsed.type === "FeatureCollection") return parsed
      if (parsed.type === "Feature") return parsed
      return { type: "Feature", geometry: parsed }
    } catch {
      return null
    }
  }, [data])

  useEffect(() => {
    if (!featureGeoJson || !mapRef.current || !mapboxReady) return

    const token = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN
    if (!token) {
      setMapError("Mapbox token not configured. Set VITE_MAPBOX_PUBLIC_TOKEN.")
      return
    }
    setMapError(null)

    try {
      const mapbox = window.mapboxgl
      mapbox.accessToken = token
      const geoJson =
        featureGeoJson.type === "FeatureCollection"
          ? featureGeoJson
          : { type: "FeatureCollection", features: [featureGeoJson] }

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }

      const map = new mapbox.Map({
        container: mapRef.current,
        style: "mapbox://styles/mapbox/outdoors-v12",
        center: [0, 0],
        zoom: 2,
      })
      mapInstanceRef.current = map

      map.on("load", () => {
        map.addSource("feature-detail", {
          type: "geojson",
          data: geoJson,
        })
        map.addLayer({
          id: "feature-detail-fill",
          type: "fill",
          source: "feature-detail",
          paint: {
            "fill-color": "#38a169",
            "fill-opacity": 0.25,
          },
          filter: ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
        })
        map.addLayer({
          id: "feature-detail-line",
          type: "line",
          source: "feature-detail",
          paint: {
            "line-color": "#38a169",
            "line-width": 2,
          },
        })
        map.addLayer({
          id: "feature-detail-point",
          type: "circle",
          source: "feature-detail",
          paint: {
            "circle-radius": 6,
            "circle-color": "#38a169",
          },
          filter: ["==", ["geometry-type"], "Point"],
        })

        const bounds = new mapbox.LngLatBounds()
        const addCoords = (coords: any) => {
          if (!coords) return
          if (typeof coords[0] === "number" && typeof coords[1] === "number") {
            bounds.extend(coords as [number, number])
            return
          }
          coords.forEach((c: any) => addCoords(c))
        }
        geoJson.features.forEach((feature: any) => addCoords(feature.geometry?.coordinates))

        if (bounds.isEmpty()) {
          const first = geoJson.features[0]?.geometry?.coordinates
          if (Array.isArray(first) && typeof first[0] === "number" && typeof first[1] === "number") {
            map.setCenter([first[0], first[1]])
            map.setZoom(13)
          }
        } else {
          map.fitBounds(bounds, { padding: 24, duration: 0 })
        }
      })
    } catch (err) {
      console.error(err)
      setMapError("Failed to render map")
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [featureGeoJson, mapboxReady])

  const mapLoadError = mapboxLoadError || mapError

  return (
    <>
      {featureGeoJson ? (
        <Box
          ref={mapRef}
          borderRadius="lg"
          overflow="hidden"
          borderWidth="1px"
          borderColor="gray.700"
          bg="gray.800"
          minH="400px"
        />
      ) : (
        <Text color="gray.400" fontSize="sm">
          No geometry available for this feature.
        </Text>
      )}
      {mapLoadError && (
        <Text color="red.300" fontSize="sm" mt="2">
          {mapLoadError}
        </Text>
      )}
    </>
  )
}
